// src/containers/Receptions/ReceptionList/components/SuborderTable.tsx
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import { useCallback, useEffect, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";

import { ExpandMoreRounded } from "@mui/icons-material";
import { Text } from "$components";
import ProductSearchInput, {
  getProductFieldMatch,
} from "$forms/ProductSearchInput";
import useReception from "$hooks/useReception";
import { ProjectServices, DockyardServices } from "$services";
import ReceptionStatus from "$types/ReceptionStatus";
import { SuborderType, SUBORDER_TYPE_LABELS } from "$types";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  orderIndex: any;
  suborderIndex: any;
  control: any;
  register: any;
  getValues: any;
  setValue: any;
  watch: any;
  errors: any;
  reception: any;
  updateOrderConformity: (
    orderIndex: number,
    suborderIndex: number,
    productIndex: number,
    value: boolean
  ) => void;
  currentProductQuantityIndex: (
    orderIdex: number,
    suborderIndex: number,
    suborderProductIndex: number
  ) => number;
  currentProductQuantityIndexInOrder: (
    orderIdex: number,
    suborderIndex: number,
    suborderProduct: any
  ) => number;
  totalUnconform: number;
  setTotalUnconform: (totalUnconform: number) => void;
  isReadOnly?: boolean;
};

type Conformity = boolean | null;

function SuborderTable({
  orderIndex,
  suborderIndex,
  control,
  register,
  getValues,
  setValue,
  watch,
  errors,
  updateOrderConformity,
  currentProductQuantityIndex,
  currentProductQuantityIndexInOrder,
  setTotalUnconform,
  reception,
  isReadOnly = false,
}: Props) {
  const { fields } = useFieldArray({
    control,
    name: `orders[${orderIndex}].suborders`,
  });
  const { classes } = useReception();
  const [sorted, setSorted] = useState(false);

  const sumUnconformProduct = useCallback(() => {
    const v = getValues();
    let total = 0;
    if (v && v.productQuantity) {
      v.productQuantity.forEach((qty: any) => {
        const number = Number(qty.unconformQuantity);
        if (!Number.isNaN(number)) {
          total += number;
        }
      });
    }
    setTotalUnconform(total);
  }, [getValues, setTotalUnconform]);

  useEffect(() => {
    sumUnconformProduct();
  }, [sumUnconformProduct]);

  const [productReference_tfilter, setProductReference_tfilter] = useState("");
  const [productDesignation_tfilter, setProductDesignation_tfilter] =
    useState("");
  const [project, setProject] = useState(null);
  const [isFilteredByProducts, setIsFilteredByProducts] = useState(false);
  const [dockyard, setDockyard] = useState(null);

  useEffect(() => {
    if (productDesignation_tfilter !== "" || productReference_tfilter !== "") {
      setIsFilteredByProducts(true);
    } else {
      setIsFilteredByProducts(false);
    }
  }, [
    setIsFilteredByProducts,
    productDesignation_tfilter,
    productReference_tfilter,
  ]);

  const fetchProjects = useCallback(async () => {
    const { project } = fields[suborderIndex] as any;
    if (project) {
      const getProject = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_PROJECT_BY_ID, project],
        queryFn: () => ProjectServices.getProjectById(project),
        staleTime: 1000 * 60,
      });
      setProject(getProject);
    }
  }, [setProject, fields, suborderIndex]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // get one dockyard
  const fetchDockyards = useCallback(async () => {
    const { dockyard } = fields[suborderIndex] as any;
    if (dockyard) {
      let getDockyard = await queryClient.getQueryData([
        CacheInvalidationAction.GET_DOCKYARD_BY_ID,
        dockyard,
      ]);
      if (!getDockyard) {
        getDockyard = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_DOCKYARD_BY_ID, dockyard],
          queryFn: () => DockyardServices.getDockyardById(dockyard),
          staleTime: 1000 * 60,
        });
      }
      setDockyard(getDockyard);
    }
  }, [setDockyard, fields, suborderIndex]);

  useEffect(() => {
    fetchDockyards();
  }, [fetchDockyards]);

  const matchToProductFilters = (indexOrder, indexSuborder, indexProduct) => {
    return (
      getProductFieldMatch(
        getValues(
          `orders[${indexOrder}].suborders[${indexSuborder}].suborderProducts[${indexProduct}].reference`
        ),
        productReference_tfilter
      ) &&
      getProductFieldMatch(
        getValues(
          `orders[${indexOrder}].suborders[${indexSuborder}].suborderProducts[${indexProduct}].name`
        ),
        productDesignation_tfilter
      )
    );
  };

  const suborderType = (fields[suborderIndex] as any).type;
  const suborderDockyard =
    suborderType === SuborderType.DOCKYARD ? dockyard : null;
  const suborderProject =
    suborderType === SuborderType.PROJECT ? project : null;
  const suborderTypeLabel =
    suborderType === SuborderType.DOCKYARD
      ? `Chantier : [${suborderDockyard?.code}] ${suborderDockyard?.name}`
      : suborderType === SuborderType.PROJECT
        ? `Projet : [${suborderProject?.code}] ${suborderProject?.name}`
        : SUBORDER_TYPE_LABELS[SuborderType.STOCK];

  const clearReceivedValues = () => {
    if (reception.status === ReceptionStatus.TODO && !isReadOnly) {
      const suborderReference = getValues(
        `orders[${orderIndex}].suborders[${suborderIndex}].reference`
      );
      const pQuantity = getValues("productQuantity");

      for (let i = 0; i < pQuantity.length; i++) {
        if (pQuantity[i].suborderReference === suborderReference) {
          setValue(`productQuantity[${i}].receivedQuantity`, "0");
        }
      }
    }
  };

  return (
    <>
      <div>
        <p>{`Sous-commande n° ${(fields[suborderIndex] as any).reference}`}</p>
        <p style={{ marginTop: -10 }}>{suborderTypeLabel}</p>
        <div style={{ marginBottom: 10, marginLeft: -10 }}>
          <Switch
            checked={sorted}
            onChange={() => {
              setSorted(!sorted);
            }}
            name="showCompany"
            color="primary"
          />
          <Text type="text">Trier par désignation</Text>
        </div>
      </div>
      <Accordion
        slotProps={{
          transition: {
            unmountOnExit: true,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          Liste des produits
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead className={classes.topFormTable}>
                <TableRow>
                  <TableCell padding="none" style={{ minWidth: 100 }}>
                    <div className={classes.productsTableCellHead}>
                      <ProductSearchInput
                        value={productReference_tfilter}
                        onChange={(e: any) => {
                          setProductReference_tfilter(e.target.value);
                        }}
                        mode="reference"
                      />
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ minWidth: 280 }}>
                    <div className={classes.productsTableCellHead}>
                      <ProductSearchInput
                        value={productDesignation_tfilter}
                        onChange={(e: any) => {
                          setProductDesignation_tfilter(e.target.value);
                        }}
                        mode="designation"
                      />
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ minWidth: 80 }}>
                    <div className={classes.productsTableCellHead}>Unité</div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 70 }}>
                    <div className={classes.productsTableCellHead}>
                      Qté
                      <small style={{ marginTop: 4 }}>attendue</small>
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 70 }}>
                    <div className={classes.productsTableCellHead}>
                      Qté
                      <small style={{ marginTop: 4 }}>déjà livrée</small>
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 70 }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <div className={classes.productsTableCellHead}>
                        Qté
                        <small style={{ marginTop: 4 }}>reçue</small>
                      </div>
                      <Button
                        color="primary"
                        disabled={
                          isReadOnly ||
                          reception.status !== ReceptionStatus.TODO
                        }
                        style={{
                          minWidth: "initial",
                          marginLeft: 6,
                        }}
                        onClick={clearReceivedValues}
                      >
                        <ClearRoundedIcon style={{ width: 18 }} />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 100 }}>
                    <div className={classes.productsTableCellHead}>
                      Conformité
                    </div>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody className={classes.bodyFormTable}>
                {isFilteredByProducts &&
                  !fields.find(() =>
                    getValues(
                      `orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts`
                    ).find((_product: any, pIndex: number) =>
                      matchToProductFilters(orderIndex, suborderIndex, pIndex)
                    )
                  ) && (
                    <TableRow>
                      <TableCell padding="none" colSpan={7}>
                        <div style={{ textAlign: "center", padding: 20 }}>
                          Aucun produit correspondant.
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                {(fields[suborderIndex] as any).suborderProducts
                  .sort((a: any, b: any) => {
                    if (sorted) {
                      return a.name
                        .toLowerCase()
                        .localeCompare(b.name.toLowerCase());
                    }
                    const aIndex = currentProductQuantityIndexInOrder(
                      orderIndex,
                      suborderIndex,
                      a
                    );
                    const bIndex = currentProductQuantityIndexInOrder(
                      orderIndex,
                      suborderIndex,
                      b
                    );

                    if (aIndex < bIndex) {
                      return -1;
                    }
                    if (aIndex > bIndex) {
                      return 1;
                    }
                    return 0;
                  })
                  .map((_suborderProduct: any, k: number) => {
                    const currentIndex = currentProductQuantityIndex(
                      orderIndex,
                      suborderIndex,
                      k
                    );
                    const lineProduct = `productQuantity[${currentIndex}]`;

                    const initialConformity = getValues(
                      `${lineProduct}.conformity`
                    );
                    const currentConformity =
                      watch(`${lineProduct}.conformity`, initialConformity) ??
                      true;

                    return !isFilteredByProducts ||
                      matchToProductFilters(orderIndex, suborderIndex, k) ? (
                      <TableRow key={lineProduct}>
                        <TableCell padding="none">
                          <Controller
                            control={control}
                            name={`orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${k}].reference`}
                            render={({ field }) => (
                              <div className={classes.tdPaddingXs}>
                                <TextField
                                  disabled
                                  variant="outlined"
                                  {...field}
                                />
                              </div>
                            )}
                          />
                        </TableCell>
                        <TableCell padding="none">
                          <div
                            title={getValues(
                              `orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${k}].name`
                            )}
                          >
                            <Controller
                              control={control}
                              name={`orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${k}].name`}
                              render={({ field }) => (
                                <div className={classes.tdPaddingXs}>
                                  <TextField
                                    disabled
                                    variant="outlined"
                                    {...field}
                                    style={{ width: "100%" }}
                                  />
                                </div>
                              )}
                            />
                          </div>
                        </TableCell>
                        <TableCell padding="none">
                          <div
                            title={getValues(
                              `orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${k}].unit`
                            )}
                          >
                            <Controller
                              control={control}
                              name={`orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${k}].unit`}
                              render={({ field }) => (
                                <div className={classes.tdPaddingXs}>
                                  <TextField
                                    disabled
                                    variant="outlined"
                                    {...field}
                                    style={{ width: "100%" }}
                                  />
                                </div>
                              )}
                            />
                          </div>
                        </TableCell>
                        <TableCell padding="none">
                          <Controller
                            control={control}
                            name={`orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${k}].quantity`}
                            render={({ field }) => (
                              <div className={classes.tdPaddingXs}>
                                <TextField
                                  disabled
                                  variant="outlined"
                                  {...field}
                                  error={
                                    errors.productQuantity !== undefined &&
                                    errors.productQuantity[
                                      currentProductQuantityIndex(
                                        orderIndex,
                                        suborderIndex,
                                        k
                                      )
                                    ] !== undefined &&
                                    errors.productQuantity[
                                      currentProductQuantityIndex(
                                        orderIndex,
                                        suborderIndex,
                                        k
                                      )
                                    ].receivedQuantity !== undefined
                                  }
                                />
                              </div>
                            )}
                          />
                        </TableCell>
                        <TableCell padding="none">
                          <div className={classes.tdPaddingXs}>
                            <Controller
                              control={control}
                              name={`${lineProduct}.alreadyReceivedQuantity`}
                              render={({ field }) => (
                                <TextField
                                  disabled
                                  variant="outlined"
                                  {...field}
                                />
                              )}
                            />
                          </div>
                        </TableCell>
                        <TableCell padding="none">
                          <div className={classes.tdPaddingXs}>
                            <Controller
                              control={control}
                              name={`${lineProduct}.receivedQuantity`}
                              render={({ field }) => (
                                <TextField
                                  disabled={
                                    isReadOnly ||
                                    !!reception?.receptionReferences?.some(
                                      (reception) => {
                                        return reception.suborderReferences?.some(
                                          (sub) => {
                                            return (
                                              sub ===
                                              getValues(
                                                `orders[${orderIndex}].suborders[${suborderIndex}].reference`
                                              )
                                            );
                                          }
                                        );
                                      }
                                    )
                                  }
                                  variant="outlined"
                                  {...field}
                                  value={field.value || 0}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                  }}
                                  error={
                                    errors.productQuantity !== undefined &&
                                    errors.productQuantity[
                                      currentProductQuantityIndex(
                                        orderIndex,
                                        suborderIndex,
                                        k
                                      )
                                    ] !== undefined &&
                                    errors.productQuantity[
                                      currentProductQuantityIndex(
                                        orderIndex,
                                        suborderIndex,
                                        k
                                      )
                                    ].receivedQuantity !== undefined
                                  }
                                />
                              )}
                            />
                          </div>
                        </TableCell>
                        <TableCell padding="none">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="hidden"
                              name={`${lineProduct}.conformity`}
                              {...register(`${lineProduct}.conformity`)}
                            />

                            <Button
                              disabled={isReadOnly}
                              color="primary"
                              style={{ minWidth: 40, marginRight: 8 }}
                              onClick={() => {
                                if (!currentConformity) {
                                  setValue(
                                    `${lineProduct}.unconformQuantity`,
                                    0
                                  );
                                  sumUnconformProduct();
                                }
                                let newValue: Conformity = null;
                                newValue = !currentConformity;
                                setValue(`${lineProduct}.conformity`, newValue);
                                updateOrderConformity(
                                  orderIndex,
                                  suborderIndex,
                                  k,
                                  newValue
                                );
                              }}
                            >
                              {currentConformity === false ? (
                                <div style={{ color: "tomato" }}>
                                  <SentimentVeryDissatisfiedIcon />
                                </div>
                              ) : (
                                <div style={{ color: "green" }}>
                                  <SentimentSatisfiedAltIcon />
                                </div>
                              )}
                            </Button>

                            {currentConformity === false && (
                              <Controller
                                control={control}
                                name={`${lineProduct}.unconformQuantity`}
                                render={({ field }) => (
                                  <TextField
                                    disabled={isReadOnly}
                                    variant="outlined"
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                    onBlur={sumUnconformProduct}
                                    error={
                                      errors.productQuantity !== undefined &&
                                      errors.productQuantity[
                                        currentProductQuantityIndex(
                                          orderIndex,
                                          suborderIndex,
                                          k
                                        )
                                      ] !== undefined &&
                                      errors.productQuantity[
                                        currentProductQuantityIndex(
                                          orderIndex,
                                          suborderIndex,
                                          k
                                        )
                                      ].unconformQuantity !== undefined
                                    }
                                  />
                                )}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null;
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export default SuborderTable;
